import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { VideoJSProps } from './VideoJS.types.ts';
import { ProgressSpinner } from 'primereact/progressspinner';
import styles from './VideoJS.module.scss';
import { $videoConnectionsStore } from '../../pages/stores/videoConnection/model.ts';
import { createVideoConnection, VideoConnection } from '../../pages/utils/video/videoConnection.tsx';
import { useLiveViewDataContext } from '../../context/LiveViewDataContext.tsx';
import { useFishEyeToSingleView } from '../../pages/LiveViewPageTrial/useFishEyeToSingleView.tsx';

import Hls, { Events, ManifestLoadedData, ManifestParsedData } from 'hls.js';

export interface VideoJSHandle {
  seek: (pos: number) => boolean;
  loadLastRecording: () => void;
  video?: HTMLVideoElement | null;
  //destroy: () => null;
}

const Component = forwardRef<VideoJSHandle, VideoJSProps>(({
  src,
  isLive,
  className,
  muted,
  onLoaded,
  onEnded,
  onTimeUpdate,
  onMetaDataLoaded,
  onLoadedMetadata,
  onCanPlay,
  onError,
  onCanPlayThrough,
  onPlaying,
  ptzOn,
  ptzCanvasRef,
  ptzParametersRef,
  wallView = false,
  fisheye,
  cameraId,
  isLoading,
  setIsLoading,
  videoRef,
  playingRecordingRef,
  lastRecordingLoading,
  setLastRecordingLoading,
}, ref) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const internalVideoRef = useRef<HTMLVideoElement | null>(null);
  const { connections } = useUnit($videoConnectionsStore);
  const [videoConnection, setVideoConnection] = useState<VideoConnection | null>(null);
  // const { setCameraStatus } = useLiveViewDataContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useImperativeHandle(ref, () => ({
    seek(pos: number) {
      if (videoConnection?.video && !isLive) {
        videoConnection.video.currentTime = pos;
        return true;
      }
      return false;
    },
    loadLastRecording() {
      if (!videoConnection?.player || !videoConnection.video) return;
      setLastRecordingLoading?.(true);
    },
    setPlaybackRate(rate: number) {
      if (videoConnection && videoConnection.video) {
        videoConnection.video.playbackRate = rate;
      }
    },
    video: videoConnection?.video ?? null,
  }));

  // Video connection setup
  useEffect(() => {
    if (!src) return;
    const isLastPlayback = playingRecordingRef?.current?.end === 'live';
    const connection = connections[src] ?? createVideoConnection(src, cameraId, isLastPlayback);
    setVideoConnection(connection);

    if (src.includes("_high")) {
      connection.video.setAttribute("data-high", "true" );
    }

    try {
      if (connection.video) {
        connection.video.playsInline = true;
        connection.video.setAttribute("playsinline", "");
        connection.video.setAttribute("webkit-playsinline", "");
        connection.video.muted = Boolean(muted);
        connection.video.setAttribute("muted", "");

        connection.video.controls = false;
        connection.video.setAttribute("controlsList", "nodownload nofullscreen noremoteplayback");

        connection.video.disablePictureInPicture = true;
        connection.video.setAttribute("disablePictureInPicture", "");
      }

      if (connection.video.currentTime > 0 && !connection.video.ended && connection.video.readyState > 2) {
        connection.video.play()?.catch((err) => {
          console.warn('[VideoJS] play() failed:', err);
        });
      }
    } catch (err) {
      console.error('[VideoJS] Error during video play attempt:', err);
    }

    return () => {
      try {
        //console.log(`[VideoJS] Unmount → destroying videoConnection for ${src}`);
        connection.destroy();
      } catch (err) {
        console.error('[VideoJS] Error during connection destroy:', err);
      }
    };
  }, [src, cameraId]);

  // Attach event listeners **only for this stream type**
  useEffect(() => {
    if (!videoConnection?.video) return;
    const video = videoConnection.video;

    // --- Shared events ---
    const timeUpdateHandler = onTimeUpdate ? (e: Event) => onTimeUpdate(e) : undefined;

    // --- Playback-only events ---
    // const metaHandler = !isLive && onMetaDataLoaded
    //   ? (hlsEvent: Events.MANIFEST_LOADED, hlsData: ManifestLoadedData) => onMetaDataLoaded(hlsEvent, hlsData, videoConnection.player)
    //   : undefined;
    const metaHandler = !isLive && onMetaDataLoaded
      ? (event: Events.MANIFEST_PARSED, data: ManifestParsedData) => {
        if (videoConnection?.player instanceof Hls) {
          onMetaDataLoaded(event, data, videoConnection.player);
        }
      }
      : undefined;
    const endedHandler = !isLive && onEnded
      ? (e: Event) => onEnded(e)
      : undefined;
    const errorHandler = !isLive && onError
      ? (hlsEvent: Events.ERROR, data: any) => onError(hlsEvent, data, videoConnection.player)
      : undefined;
    const canPlayThroughHandler = !isLive && onCanPlayThrough
      ? (e: Event) => onCanPlayThrough(e)
      : undefined;

    // --- Live or playback events ---
    const canPlayHandler = onCanPlay
      ? (e: Event) => {
        onCanPlay(e);
        onLoaded?.(e);
      }
      : undefined;

    const loadedMetadataHandler = onLoadedMetadata
      ? (e: Event) => onLoadedMetadata(e)
      : undefined;

    const playingHandler = (e: Event) => {
      setLastRecordingLoading?.(false);
      setIsLoading?.(false); // ensure spinner hides
      if (onPlaying) {
        onPlaying(e);  // notify parent spinner
      }
    };

    // Attach listeners
    if (metaHandler) {
      if (videoConnection.player instanceof Hls) {
        // videoConnection.player?.on(Hls.Events.MANIFEST_LOADED, metaHandler);
        videoConnection.player?.on(Hls.Events.MANIFEST_PARSED, metaHandler);
      }

    }
    if (endedHandler) {
      video.addEventListener('ended', endedHandler);
    }
    if (errorHandler) {
      if (videoConnection.player instanceof Hls) {
        videoConnection.player?.on(Hls.Events.ERROR, errorHandler);
      }
    }

    if (canPlayHandler) video.addEventListener('canplay', canPlayHandler);
    video.addEventListener('playing', playingHandler);
    if (canPlayThroughHandler) video.addEventListener('canplaythrough', canPlayThroughHandler);
    if (timeUpdateHandler) video.addEventListener('timeupdate', timeUpdateHandler);
    if (loadedMetadataHandler) video.addEventListener("loadedmetadata", loadedMetadataHandler);

    return () => {
      if (metaHandler) {
        if (videoConnection.player instanceof Hls) {
          // videoConnection.player.off(Hls.Events.MANIFEST_LOADED, metaHandler);
          videoConnection.player.off(Hls.Events.MANIFEST_PARSED, metaHandler)
        }
      }

      // if (canPlayThroughHandler) video.removeEventListener('canplaythrough', canPlayThroughHandler);
      if (endedHandler) {
        video.removeEventListener('ended', endedHandler);
        // video.removeEventListener('error', endedHandler);
      }
      if (errorHandler) {
        if (videoConnection.player instanceof Hls) {
          videoConnection.player?.off(Hls.Events.ERROR, errorHandler);
        }
      }
      if (canPlayHandler) video.removeEventListener('canplay', canPlayHandler);
      video.removeEventListener('playing', playingHandler);
      if (canPlayThroughHandler) video.removeEventListener("canplaythrough", canPlayThroughHandler);
      if (timeUpdateHandler) video.removeEventListener('timeupdate', timeUpdateHandler);
      if (loadedMetadataHandler) video.removeEventListener("loadedmetadata", loadedMetadataHandler);
    };
  }, [
    videoConnection,
    isLive,
    onMetaDataLoaded,
    onEnded,
    onTimeUpdate,
    onCanPlay,
    onCanPlayThrough,
    onError,
    onLoadedMetadata,
    onPlaying,
  ]);

  // DOM rendering of the video element
  useEffect(() => {
    if (wrapperRef.current && videoConnection?.video) {
      videoConnection.video.muted = Boolean(muted);
      videoConnection.video.className = className ?? '';
      wrapperRef.current.appendChild(videoConnection.video);
      videoRef.current = videoConnection.video;

      if (videoRef.current.paused) {
        setTimeout(() => {
          const video = videoRef.current;
          if (!video) return;
          video.play()?.catch(err => {
            if (
              err?.message?.includes("The play() request was interrupted") ||
              err?.name === "AbortError"
            ) {
              console.warn("[Suppressed play() interruption]:", err.message);
              return;
            }

            // Log or show actual errors
            console.error("Unhandled video error:", err);
          });
        }, 100);
      }

    }
    return () => {
      if (videoConnection?.video) videoConnection.video.muted = true;
      if (wrapperRef.current) wrapperRef.current.innerHTML = '';
    };
  }, [videoConnection, muted, className]);

  // High Stream support (for live only)
  // useEffect(() => {
  //   if (!wrapperRef.current || !isLive) return;
  //   const vc = createVideoConnection(src, cameraId, true, true);
  //   vc.video.setAttribute("data-high", src);

  //   const highHandler = () => {
  //     if (wrapperRef.current) {
  //       setVideoConnection(vc);
  //     }
  //   };

  //   vc.video.addEventListener("canplay", highHandler, { once: true });

  //   return () => {
  //     vc.destroy(true);
  //   }
  // }, [src, wrapperRef, cameraId, isLive]);

  // Clean up high stream when unmount
  // useEffect(() => {
  //   return () => {
  //     if (
  //       videoConnection &&
  //       videoConnection.video &&
  //       videoConnection.video.getAttribute("data-high")
  //     ) {
  //       videoConnection.destroy(true);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const video = videoConnection?.video;

    if (!wrapperRef.current || !video) return;

    video.muted = Boolean(muted);
    video.className = className ?? '';

    const alreadyAttached = wrapperRef.current.contains(video);
    if (!alreadyAttached) {
      wrapperRef.current.innerHTML = '';
      wrapperRef.current.appendChild(video);
    }

    internalVideoRef.current = video;

    return () => {
      //clearTimeout(timeoutId);
      if (video) video.muted = true;
      if (wrapperRef.current) wrapperRef.current.innerHTML = '';
      // videoConnection?.destroy(true);
      // if (videoConnection) {
      //   console.log(`[VideoJS] Destroying videoConnection for ${src}`);
      //   videoConnection.destroy(true);
      // }
    };
  }, [videoConnection?.video, className, muted]);

  useFishEyeToSingleView({
    videoRef,
    canUsePTZ: Boolean(src && ptzOn && !lastRecordingLoading),
    containerRef: wrapperRef,
    ptzCanvasRef,
    ptzParametersRef,
    isLoading: isLoading,
    setIsLoading: setIsLoading,
  });

  //  useVideoZoom({
  //   wrapperRef,
  //   containerRef: wrapperRef, // same element for both
  //   videoRef,
  //   ptzOn,
  //   connection: videoConnection,
  //   transformRef
  // });

  return (
    <>
      {!isLive && (lastRecordingLoading) && (
        <div
        className='flex items-center'
        >
          <ProgressSpinner
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: '60px',
              height: '60px',
              color: "white"
            }}
            strokeWidth="2"
            animationDuration=".5s"
          />
        </div >
      )}
      <div
        className={clsx(styles.videoWrapper, {
          [styles.fisheye]: src && (ptzOn || (wallView && fisheye)),
          [styles.hidden]: lastRecordingLoading,
        })}
        ref={wrapperRef}
      ></div>
    </>
  );
});

export const VideoJS = memo(Component);